/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCommentsTree } from '../../../../../state/thunks/commenting';
// utils
import { url } from '../../../../../utils/urls';
import { capitalizeString } from '../../../../../utils/string';
// material
import { Group, CardControl, IncDecField, Control, ListTable } from '@insticator/insticator-ui';
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller'
import ExpandableSection from '../../../../../material/expandablesection/expandablesection'
import SectionDescription from '../../../../../material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription'
// styles
import injectSheet from 'react-jss';
import styles from './commentstreesection_styles'

const limitOptions = [5, 10, 15, 20, 25];

const CommentsTreeSection = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const { 
    data: { commentsTree: configCommentsTree }, 
    isUpdating 
  } = useSelector(state => state.commenting.commentingConfig)

  const [commentsTree, setCommentsTree] = useState(configCommentsTree)
  const [customLimit, setCustomLimit] = useState({comments: 15, replies: 5})
  const [isOpened, setIsOpened] = useState(false);


  const handleToggle = () => setIsOpened(!isOpened)

  const handleSave = useCallback(async () => {
    const response = await dispatch(updateCommentsTree({commentTreeConfigurationUUID: configCommentsTree.uuid, commentsTree}))
    setIsOpened(false)
  })

  const handleCancel = () => {
    setIsOpened(false)
    setCommentsTree(configCommentsTree)
  }

  const handleSelectTreeType = type => {
    let selectedTreeTypeLimits = {
      commentsInitialLoadLimit: 5,
      repliesInitialLoadLimit: 1
    }

    switch(type) {
      case 'BASIC':
        selectedTreeTypeLimits.commentsInitialLoadLimit = 5;
        selectedTreeTypeLimits.repliesInitialLoadLimit = 1;
        break;
      case 'COMPACT':
        selectedTreeTypeLimits.commentsInitialLoadLimit = 2;
        selectedTreeTypeLimits.repliesInitialLoadLimit = 0;
        break;
      case 'ADVANCED':
        selectedTreeTypeLimits.commentsInitialLoadLimit = 10;
        selectedTreeTypeLimits.repliesInitialLoadLimit = 3;
        break;
      case 'CUSTOM':
        selectedTreeTypeLimits.commentsInitialLoadLimit = customLimit.comments;
        selectedTreeTypeLimits.repliesInitialLoadLimit = customLimit.replies
        break;
      default:
        break;
    }

    setCommentsTree({
      ...commentsTree,
      treeType: type,
      commentsInitialLoadLimit: selectedTreeTypeLimits.commentsInitialLoadLimit,
      repliesInitialLoadLimit: selectedTreeTypeLimits.repliesInitialLoadLimit
    })
  }

  const handleCustomIncDec = (val,type) => {
    setCustomLimit({
      ...customLimit,
      [type]: parseInt(val)
    })
    setCommentsTree({
      ...commentsTree,
      [`${type}InitialLoadLimit`]: parseInt(val)
    })
  }

  const isChanged = useMemo(() => {
    const isFieldsChanged = !(JSON.stringify(commentsTree) === JSON.stringify(configCommentsTree));
    return isFieldsChanged
  }, [commentsTree, configCommentsTree])

  const controller = (
    <ConfigController
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.commentsTree}
      disableSave={!isChanged}
      title='Comments Tree'
      description={<SectionDescription
          main={capitalizeString(configCommentsTree.treeType)}
          additional={`${configCommentsTree.commentsInitialLoadLimit} ${configCommentsTree.commentsInitialLoadLimit > 1 ? 'comments' : 'comment'} / 
          ${configCommentsTree.repliesInitialLoadLimit} ${configCommentsTree.repliesInitialLoadLimit > 1 ? 'replies' : 'reply'}`}
      />}
    />
  );

  return (
    <ExpandableSection
      className={`commentstreesection ${props.classes.commentstreesection}`}
      controller={controller}
      isOpened={isOpened}
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isUpdating.commentsTree}
      disableSave={!isChanged}
    >
      <div className={'commentstreecontent'}>
        <div className={'commentstreeoptions'}>
            <SectionGroup>
              <Group>
              <CardControl
                title={'Basic'}
                description={'Perfect for any type of community. Create a balance between showing the most recent comments, and inviting to join a conversation.'}
                tips={'(entertainment sites, e-commerce sites, etc.)'}
                image={`${url.image}/illustrations/illustration-basic.svg`}
                value={'BASIC'}
                handleSelect={handleSelectTreeType}
                selected={commentsTree.treeType === 'BASIC'}
                recommended={true}
              />
              <CardControl
                title={'Compact'}
                description={'Show enough unique opinions to spark a conversation; great for limited real estate.'}
                tips={'(personal blogs, product sites, etc.)'}
                image={`${url.image}/illustrations/illustration-compact.svg`}
                value={'COMPACT'}
                handleSelect={handleSelectTreeType}
                selected={commentsTree.treeType === 'COMPACT'}
              />
              <CardControl
                title={'Advanced'}
                description={'Show the most detailed conversation thread, so replies don’t go unnoticed and your readers get the full context of the conversation.'}
                tips={'(news sites, social sites, educational sites, etc.)'}
                image={`${url.image}/illustrations/illustration-advanced.svg`}
                value={'ADVANCED'}
                handleSelect={handleSelectTreeType}
                selected={commentsTree.treeType === 'ADVANCED'}
              />
              { isOpened &&
                  <CardControl
                    title={'Custom'}
                    description={'Define your own presets for how many comments and replies should be visible by default.'}
                    content={
                        <div
                          className={'customfields'}
                          onClick={e => {
                              e.stopPropagation();
                              if (commentsTree.treeType !== 'CUSTOM') handleSelectTreeType('CUSTOM')
                          }}
                        >
                          <div className={`customfieldscontent`}>
                            <label>Visible Comments</label>
                            <IncDecField
                              key={commentsTree.treeType}
                              name={'visiblecomments'}
                              defaultValue={customLimit.comments}
                              disabled={commentsTree.treeType !== 'CUSTOM'}
                              minValue={1}
                              maxValue={25}
                              handleIncToggle={val => handleCustomIncDec(val, 'comments')}
                              handleDecToggle={val => handleCustomIncDec(val, 'comments')}
                              ctaPosition={'inline'}
                            />
                          </div>
                          <div className={`${commentsTree.treeType !== 'CUSTOM' ? 'disabled' : ''} customfieldscontent`}>
                            <label>Visible Replies</label>
                            <IncDecField
                              key={commentsTree.treeType}
                              name={'visiblereplies'}
                              defaultValue={customLimit.replies}
                              disabled={commentsTree.treeType !== 'CUSTOM'}
                              minValue={0}
                              maxValue={25}
                              handleIncToggle={val => handleCustomIncDec(val, 'replies')}
                              handleDecToggle={val => handleCustomIncDec(val, 'replies')}
                              ctaPosition={'inline'}
                            />
                          </div>
                      </div>
                    }
                    tips={'(max value for each item is 25)'}
                    image={`${url.image}/illustrations/illustration-custom.svg`}
                    value={'CUSTOM'}
                    handleSelect={handleSelectTreeType}
                    selected={commentsTree.treeType === 'CUSTOM'}
                  />
              }
            </Group>
          </SectionGroup>
        </div>

        <div className={'commentstreepreview'}>
          <SectionCard>
            <div className={'previewcontent'}>
              <h2>{capitalizeString(commentsTree.treeType)} tree</h2>
              <p>Visible by default</p>
              <ListTable
                name={'limitcounts'}
                type={'standard'}
                variant={'standard'}
                size={'full'}
                paginationLocation={'bottom'}
                data={[
                  {
                    comments: commentsTree.commentsInitialLoadLimit,
                    replies: commentsTree.repliesInitialLoadLimit
                  }]}
                columnHeads={{
                  comments: <>
                    <span className={'headicon commentsicon'}>C</span>
                    <span className={'headtext commentstext'}>Comments</span>
                  </>,
                  replies: <>
                    <span className={'headicon repliesicon'}>R</span>
                    <span className={'headtext repliestext'}>Replies</span>
                  </>,
                }}
                rowsPerPage={1}
                fixedColWidth={50}
                moreDetails={false}
                loading={false}
              />
              <p>Structure</p>
              <img
                className={'previewimg'}
                src={`${url.image}/illustrations/illustration-${commentsTree.treeType === 'CUSTOM' ? `${commentsTree.treeType.toLowerCase()}- tree` : `${commentsTree.treeType.toLowerCase()}-tree`}.svg`}
              />
            </div>
          </SectionCard>
        </div>
      </div>


      <div className={'limitsection'}>
        <SectionCard>
          <div className={'commentslimit'}>
            <img className={'commentslimiticon'} src={`${url.image}/illustrations/illustration-limit.svg`} />
            <SectionGroup>
              <SectionGroupDescription
                title='Comments Limit'
                description="Define how many comments should be visible after pressing 'Load more comments’."
              />
              <div className={'controlgroup'}>
                <Group>
                { limitOptions.map(value =>
                  <Control
                    text={value.toString()}
                    value={value}
                    handleSelect={() => setCommentsTree({...commentsTree, commentsLoadMoreLimit: parseInt(value)})}
                    variant={'secondary'}
                    selected={parseInt(commentsTree.commentsLoadMoreLimit) === value}
                  />
                )}
                </Group>
              </div>
            </SectionGroup>
          </div>
          <div className={'replieslimit'}>
            <SectionGroup>
              <SectionGroupDescription
                title='Replies Limit'
                description="Define how many replies should be visible after pressing 'Show more replies'."
              />
              <div className={'controlgroup'}>
                <Group>
                { limitOptions.map(value =>
                  <Control
                    text={value.toString()}
                    value={value}
                    handleSelect={() => setCommentsTree({...commentsTree, repliesLoadMoreLimit: parseInt(value)})}
                    variant={'secondary'}
                    selected={parseInt(commentsTree.repliesLoadMoreLimit) === value}
                  />
                )}
                </Group>
              </div>
            </SectionGroup>
          </div>
        </SectionCard>
      </div>
    </ExpandableSection>
  )
})  

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default CommentsTreeSection;